/**
 * Entity selection constants and type utilities
 * Used for Include/Exclude selection in tree selectors and autocomplete components
 */

/* Constants */
export const ENTITY_SELECTION_TYPE = {
    POSITIVE: 'EntityPositive',
    NEGATIVE: 'EntityNegative',
} as const

/* Inferred Types */
export type EntitySelectionType = typeof ENTITY_SELECTION_TYPE[keyof typeof ENTITY_SELECTION_TYPE]

/* Type Guards */
const entitySelectionValues = new Set<string>(Object.values(ENTITY_SELECTION_TYPE))

export function isEntitySelectionType (value: unknown): value is EntitySelectionType {
    return typeof value === 'string' && entitySelectionValues.has(value)
}

/* Helpers */
/**
 * Returns the entity selection type from a saved value, with a default fallback
 * @param value - The saved value to check
 * @param defaultType - Default type if value is invalid (defaults to NEGATIVE/Exclude)
 */
export function getEntitySelectionType (
    value: unknown,
    defaultType: EntitySelectionType = ENTITY_SELECTION_TYPE.NEGATIVE
): EntitySelectionType {
    return isEntitySelectionType(value) ? value : defaultType
}

/**
 * Determines entity selection type with backward compatibility fallback.
 * Priority:
 * 1. If EntityTypeSelection is explicitly saved, use it
 * 2. Otherwise, auto-detect based on which list has data (backward compatibility)
 * 3. If nothing selected, default to EXCLUDE (NEGATIVE)
 *
 * @param savedSelection - The saved EntityTypeSelection value
 * @param positiveList - The EntityPositive list
 * @param negativeList - The EntityNegative list
 */
export function resolveEntitySelectionType (
    savedSelection: unknown,
    positiveList: unknown[] | undefined,
    negativeList: unknown[] | undefined
): EntitySelectionType {
    // 1. If saved selection exists, use it
    if (isEntitySelectionType(savedSelection)) {
        return savedSelection
    }
    // 2. Fallback: auto-detect based on which list has data (backward compatibility)
    if (negativeList?.length) {
        return ENTITY_SELECTION_TYPE.NEGATIVE
    }
    if (positiveList?.length) {
        return ENTITY_SELECTION_TYPE.POSITIVE
    }
    // 3. Default to EXCLUDE
    return ENTITY_SELECTION_TYPE.NEGATIVE
}

/**
 * Returns the opposite entity selection type
 */
export function getOppositeEntityType (type: EntitySelectionType): EntitySelectionType {
    return type === ENTITY_SELECTION_TYPE.POSITIVE
        ? ENTITY_SELECTION_TYPE.NEGATIVE
        : ENTITY_SELECTION_TYPE.POSITIVE
}

